"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { useState } from "react";

interface SpecializationCardProps {
  icon?: string;
  hoverIcon?: string;
  altIcon?: string;
  title: string;
  description: string;
  learnMoreLabel?: string;
  ctaTextColor?: string;
  cardColor?: string;
  cardHoverColor?: string;
  cardTitleColor?: string;
  cardTitleHoverColor?: string;
  mobileCardTitleColor?: string;
  cardDescriptionColor?: string;
  cardDescriptionHoverColor?: string;
  mobileCardDescriptionColor?: string;
  isFlipped?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export default function SpecializationCard(data: SpecializationCardProps) {
  const {
    icon,
    hoverIcon,
    altIcon,
    title,
    description,
    learnMoreLabel = "Learn More",
    ctaTextColor,
    cardColor,
    cardHoverColor,
    cardTitleColor,
    cardTitleHoverColor,
    mobileCardTitleColor,
    cardDescriptionColor,
    cardDescriptionHoverColor,
    mobileCardDescriptionColor,
    isFlipped,
    onClick,
    onMouseEnter,
    onMouseLeave,
  } = data;

  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
    onMouseEnter?.();
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    onMouseLeave?.();
  };

  const learnMoreColor = ctaTextColor?.trim() || cardHoverColor || "#F74958";
  const backBg = cardHoverColor || cardColor;
  const backTextColor = mobileCardDescriptionColor || "#FFFFFF";
  const titleColorMobile = mobileCardTitleColor || cardTitleColor;

  /* Viewport &lt; 768px only — flip card (useIsMobile(768) in parent). Project sm/md/lg/xl = 1080/1230/1430/1750. */
  const mobileCard = (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={Boolean(isFlipped)}
      aria-label={`${title}. ${learnMoreLabel}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={cn(
        "relative block min-w-0 w-full cursor-pointer perspective-[1000px] touch-manipulation outline-none tab:hidden",
        "focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-main",
        "h-auto",
      )}
    >
      <div
        className={cn(
          "grid w-full grid-cols-1 transition-transform duration-500 ease-out [transform-style:preserve-3d]",
          isFlipped ? "transform-[rotateY(180deg)]" : "",
        )}
      >
        <div
          className="backface-hidden col-start-1 row-start-1 min-w-0 w-full"
          style={{
            WebkitBackfaceVisibility: "hidden",
            backfaceVisibility: "hidden",
          }}
        >
          <div
            className="flex w-full flex-col overflow-hidden rounded-3xl border border-[#e4e4e4] p-4 shadow-[0px_4px_14.8px_0px_rgba(0,0,0,0.12)]"
            style={{ backgroundColor: cardColor }}
          >
            <div className="flex min-w-0 w-full flex-col items-center gap-4 text-center">
              {icon ? (
                <div className="relative size-[52px] shrink-0">
                  <Image
                    src={icon}
                    alt={altIcon || title}
                    fill
                    className="object-contain"
                    sizes="52px"
                  />
                </div>
              ) : (
                <div className="size-[52px] shrink-0" aria-hidden />
              )}
              <p
                className="m-0 w-full min-w-0 overflow-hidden text-center text-base font-semibold leading-snug tracking-tight line-clamp-3 whitespace-pre-wrap type-h6"
                style={{ color: titleColorMobile }}
              >
                {title}
              </p>
              <span
                className="mt-1 text-sm font-normal underline decoration-solid underline-offset-4 pointer-events-none"
                style={{ color: learnMoreColor }}
              >
                {learnMoreLabel}
              </span>
            </div>
          </div>
        </div>

        <div
          className="backface-hidden col-start-1 row-start-1 min-w-0 w-full transform-[rotateY(180deg)]"
          style={{
            WebkitBackfaceVisibility: "hidden",
            backfaceVisibility: "hidden",
          }}
        >
          <div
            className="flex min-h-full w-full flex-col items-center justify-center rounded-3xl border border-[#e4e4e4] p-4 shadow-[0px_4px_14.8px_0px_rgba(0,0,0,0.12)] type-body-sm"
            style={{ backgroundColor: backBg }}
          >
            <p
              className="m-0 w-full min-w-0 overflow-hidden text-center text-sm font-normal leading-relaxed line-clamp-5"
              style={{ color: backTextColor }}
            >
              {description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  /* Viewport ≥ 768px — original card; sm/md/lg/xl follow theme (1080 / 1230 / 1430 / 1750). */
  const desktopCard = (
    <div
      onClick={onClick}
      className={cn(
        "relative hidden min-w-0 w-full cursor-pointer perspective-[1000px] tab:block",
        "xs:h-[124px]",
        "sm:h-[254px]",
        "md:h-[282px] lg:h-[266px] xl:h-[266px]",
      )}
    >
      <div
        className={cn(
          "relative h-full w-full transition-transform duration-500",
          isFlipped ? "[transform:rotateY(180deg)]" : "",
        )}
        style={{ transformStyle: "preserve-3d" }}
      >
        <div
          className="absolute inset-0 h-full w-full [backface-visibility:hidden]"
          style={{
            WebkitBackfaceVisibility: "hidden",
            backfaceVisibility: "hidden",
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div
            className={cn(
              "flex h-full w-full flex-col justify-start overflow-hidden rounded-[18px]",
              "border border-[#e4e4e4]",
              "shadow-[0px_4px_14.8px_0px_rgba(0,0,0,0.15)]",
              "transition-all duration-300",
              "p-4 xs:p-[14px]",
              "sm:p-[24px] sm:gap-[12px]",
              "md:p-[24px] md:gap-[12px]",
              "lg:p-[24px] lg:gap-[12px] xl:p-[24px] xl:gap-[12px]",
            )}
            style={{
              backgroundColor: isHovered ? cardHoverColor : cardColor,
            }}
          >
            <div
              className={cn(
                "flex w-full min-w-0 shrink-0 flex-col items-center justify-start",
                "gap-2 xs:gap-1.5",
                "sm:h-[125px] sm:gap-[10px]",
                "md:h-[143px] md:justify-center xl:h-[143px] xl:justify-center",
              )}
            >
              {icon ? (
                <div
                  className={cn(
                    "relative h-11 w-11 shrink-0 xs:h-10 xs:w-10",
                    "sm:size-[52px] md:size-[52px] lg:size-[52px] xl:size-[52px]",
                  )}
                >
                  <Image
                    src={isHovered && hoverIcon ? hoverIcon : icon}
                    alt={altIcon || title}
                    fill
                    className="object-contain transition-all duration-300"
                    sizes="(min-width: 1024px) 52px, 44px"
                  />
                </div>
              ) : (
                <div className="shrink-0 sm:size-[52px]" aria-hidden />
              )}
              <p
                className={cn(
                  "w-full min-w-0 overflow-hidden text-center text-body-1-semibold line-clamp-2 transition-colors duration-300",
                  "sm:text-[14px] sm:font-semibold sm:leading-normal sm:line-clamp-3",
                  "md:text-[16px] md:font-semibold md:leading-normal md:line-clamp-3",
                  "lg:text-[18px] lg:font-semibold lg:leading-normal lg:line-clamp-3",
                  "xl:text-[18px] xl:font-semibold xl:leading-normal xl:line-clamp-3 type-h6",
                )}
                style={{
                  color:
                    isHovered && cardTitleHoverColor
                      ? cardTitleHoverColor
                      : cardTitleColor,
                }}
              >
                {title}
              </p>
            </div>
            <div
              className={cn(
                "w-full min-w-0 shrink-0",
                "hidden sm:block",
                "lg:h-[63px] lg:max-h-[63px]",
                "xl:h-[63px] xl:max-h-[63px]",
              )}
            >
              <p
                className={cn(
                  "m-0 w-full min-w-0 overflow-hidden p-0 text-center text-body-3-regular transition-colors duration-300",
                  "line-clamp-2",
                  "sm:text-center sm:text-[12px] sm:font-normal sm:leading-normal sm:line-clamp-3",
                  "md:text-[14px] md:font-normal md:leading-normal md:line-clamp-3",
                  "lg:text-[14px] lg:font-normal lg:leading-normal lg:line-clamp-3",
                  "xl:text-[14px] xl:font-normal xl:leading-normal xl:line-clamp-3 type-body-sm",
                )}
                style={{
                  color:
                    isHovered && cardDescriptionHoverColor
                      ? cardDescriptionHoverColor
                      : cardDescriptionColor,
                  margin: 0,
                  padding: 0,
                }}
              >
                {description}
              </p>
            </div>
          </div>
        </div>

        <div
          className="absolute h-full w-full [transform:rotateY(180deg)] [backface-visibility:hidden] sm:hidden"
          style={{
            WebkitBackfaceVisibility: "hidden",
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          <div
            className={cn(
              "flex h-full w-full flex-col items-center justify-center rounded-[18px] p-4 shadow-md",
            )}
            style={{ backgroundColor: cardHoverColor }}
          >
            <p
              className="text-body-3-regular text-center sm:text-body-2-regular line-clamp-4 type-body-sm"
              style={{ color: mobileCardDescriptionColor || "#FFFFFF" }}
            >
              {description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative min-w-0 w-full">
      {mobileCard}
      {desktopCard}
    </div>
  );
}
